/**
 * ZPL II (Zebra, Argox PPLZ emülasyonu) komut üreticisi.
 *
 * Grafik basımı PPLB gibi tek adımlı: `^GF` rasteri doğrudan görüntü alanına
 * yazıyor. PPLA/PPLB'den ayrıldığı iki nokta var:
 *
 *  1) **Bit polaritesi ters.** ZPL'de `1 = siyah`, `0 = beyaz`
 *     (Zebra: "Each '1' in the binary field will be printed"). PPLA ve PPLB'de
 *     bunun tersi geçerli, o yüzden raster dönüşümü bu dosyada ayrı.
 *  2) **Veri ASCII hex gidiyor.** `^GF`'in binary modu (`B`) daha kompakt ama
 *     ham veri içine düşen `^`, `~` ve `,` byte'ları komut ayrıştırıcısını
 *     bozuyor; kaçış yönetmek yerine hex tercih edildi. Bedeli iki kat veri
 *     boyutu: 40×25 mm etiket 203 dpi'de ~8 KB raster → ~16 KB hex.
 *
 * `^GFa,b,c,d,data` parametreleri:
 *   a = sıkıştırma tipi (A = ASCII hex)
 *   b = gönderilecek toplam byte (hex modda c ile aynı olmalı)
 *   c = görüntünün byte cinsinden boyutu (satır başına byte × satır)
 *   d = satır başına byte
 *
 * Satırlar üstten aşağı, her byte'ın en anlamlı biti en soldaki dot.
 */

import { MonoBitmap } from './bmp1bit';
import { ascii, clamp, concat } from './byteUtils';

const LF = '\n';

export interface ZplJobOptions {
  /** Basılacak kopya sayısı (1-9999) */
  copies?: number;
  /**
   * Koyuluk 0-30 (mutlak). `~SD` ile gönderilir ve **yazıcının ayarını kalıcı
   * olarak değiştirir** — göreli olan `^MD` mutlak bir değeri ifade edemiyor.
   * Verilmezse hiç gönderilmez.
   */
  darkness?: number;
  /** Baskı hızı ips (1-14, model bağımlı). Verilmezse yazıcı ayarı. */
  speed?: number;
  /** Etiketin sol kenarından uzaklık (dot) */
  offsetXDots?: number;
  /** Etiketin üst kenarından uzaklık (dot) */
  offsetYDots?: number;
  /** Yazdırma genişliği (dot). Verilmezse yazıcının tam kafa genişliği. */
  labelWidthDots?: number;
  /**
   * Etiket uzunluğu (dot). Verilmezse yazıcının kendi ölçümü korunur —
   * yanlış bir uzunluk etiketleri kaydırdığı için varsayılan olarak
   * gönderilmiyor.
   */
  labelLengthDots?: number;
}

export interface ZplRasterResult {
  /** ASCII hex gövde (büyük harf, ayırıcısız) */
  hex: string;
  /** Satır başına byte (^GF d parametresi) */
  bytesPerRow: number;
  /** Görüntünün byte cinsinden boyutu (^GF b ve c parametreleri) */
  byteCount: number;
}

const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).toUpperCase().padStart(2, '0'));

/** Mürekkep matrisini `^GF` gövdesine (ASCII hex) çevirir */
export function encodeZplRaster(mono: MonoBitmap): ZplRasterResult {
  const { width, height, ink } = mono;
  if (width <= 0 || height <= 0) {
    throw new Error('Geçersiz bitmap ölçüsü');
  }

  const bytesPerRow = Math.ceil(width / 8);
  const parts: string[] = [];

  for (let y = 0; y < height; y++) {
    const srcBase = y * width;
    for (let b = 0; b < bytesPerRow; b++) {
      let value = 0;
      const xBase = b << 3;
      for (let bit = 0; bit < 8; bit++) {
        const x = xBase + bit;
        // Satır sonundaki dolgu bitleri 0 kalır, yani beyaz
        if (x < width && ink[srcBase + x] !== 0) {
          value |= 1 << (7 - bit);
        }
      }
      parts.push(HEX[value]);
    }
  }

  return {
    hex: parts.join(''),
    bytesPerRow,
    byteCount: bytesPerRow * height,
  };
}

/** Tek bir bitmap etiketi için tam ZPL iş akışını üretir */
export function buildZplBitmapJob(mono: MonoBitmap, options?: ZplJobOptions): Uint8Array {
  const raster = encodeZplRaster(mono);
  const copies = clamp(options?.copies ?? 1, 1, 9999);

  const chunks: Uint8Array[] = [];
  const push = (s: string) => chunks.push(ascii(s));

  // Tilde komutları etiket formatının dışında, hemen işlenir
  if (options?.darkness !== undefined) {
    push(`~SD${clamp(options.darkness, 0, 30)}${LF}`);
  }

  push(`^XA${LF}`);
  // Yazıcıda kayıtlı bir başlangıç ötelemesi çıktının yerini kaydırmasın
  push(`^LH0,0${LF}`);

  if (options?.labelWidthDots) {
    push(`^PW${Math.round(options.labelWidthDots)}${LF}`);
  }
  if (options?.labelLengthDots) {
    push(`^LL${Math.round(options.labelLengthDots)}${LF}`);
  }
  if (options?.speed !== undefined) {
    push(`^PR${clamp(options.speed, 1, 14)}${LF}`);
  }

  const x = Math.max(0, Math.round(options?.offsetXDots ?? 0));
  const y = Math.max(0, Math.round(options?.offsetYDots ?? 0));
  push(`^FO${x},${y}${LF}`);
  push(`^GFA,${raster.byteCount},${raster.byteCount},${raster.bytesPerRow},${raster.hex}${LF}`);
  push(`^FS${LF}`);

  if (copies > 1) push(`^PQ${copies}${LF}`);
  push(`^XZ${LF}`);

  return concat(chunks);
}

/** Yazıcıyı bilinen bir duruma getirir — sıkışma sonrası kurtarma için */
export function buildZplReset(): Uint8Array {
  return ascii(`~JA${LF}`);
}
